import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import withApi from '../../components/higherOrderComponents/withApi';
import { readQuery } from '../query';
import ListTools from '../components/ListTools';
import ActiveFilters from '../components/ActiveFilters';
import EmptyRow from '../components/EmptyRow';
import { participantShare } from '../format';
import Guid from '../components/Guid';
import Pager from '../components/Pager';
import StatusPill from '../components/StatusPill';
import LookupSelect from '../components/LookupSelect';
import { lookupFacility, lookupProvider, lookupUser } from '../lookups';
import { purchaseParticipantStatus } from '../../enums/purchaseParticipantStatus';
import { purchaseParticipantType } from '../../enums/purchaseParticipantType';

/**
 * Participants — who is on a purchased product.
 *
 * Fourteen filters, all of the old page's, laid out in the design's grid rather
 * than behind a "More filters" toggle: on this page the date ranges are the
 * point, not an afterthought, so hiding four of the six would be the wrong
 * default. (The design draws thirteen — it has no Purchase id. Kept, because
 * "everyone on this purchase" is the search this page most obviously answers.)
 *
 * Provider, user and facility are lookups, as they are in the old page: you type
 * a name and the id goes into the URL. See `LookupSelect` — the design draws them
 * as selects, which they cannot be at this scale.
 *
 * Two things the endpoint decides, and this page states rather than papers over:
 *
 *   - **No total.** `/admin/participants/search` answers a bare array, so the
 *     pager offers a next page while the current one comes back full. The
 *     design's "Pagina 1 di 34.567" would be a number nobody has.
 *   - **Order is per page, not overall.** The query applies `Skip`/`Take` and
 *     only then `OrderByDescending(CreatedAt)`, so *which* rows a page holds is
 *     decided unordered and the sort happens within it. Page 2 is therefore not
 *     "the next fifteen oldest". Filter to narrow instead of paging deep; noted
 *     in REWRITE-STATE.md as a backend fix.
 */

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');



/**
 * What `ParticipantsController` can sort by. The list is short on purpose: it
 * offers only columns this page shows, because sorting by something invisible
 * produces an order nobody can explain.
 *
 * `created` is not a case in the switch — it is the default, which is
 * `CreatedAt`, and the column is labelled for what it sorts by rather than for
 * the branch it happens to take.
 */
const SORTABLE = {
  Created: 'created',
  Occurs: 'occursAt',
  Invitee: 'invitee',
  Status: 'status',
  Type: 'type',
  Slots: 'membershipSlots'
};

const TEXT_FILTERS = [
  { name: 'participantId', label: 'Id', placeholder: 'Find by id' },
  { name: 'purchaseId', label: 'Purchase id', placeholder: 'Find by purchase id' },
  { name: 'purchaseProductId', label: 'Purchase product id', placeholder: 'Find by purchase product id' }
];

const DATE_FILTERS = [
  { name: 'afterAt', label: 'Created from' },
  { name: 'beforeAt', label: 'Created to' },
  { name: 'billingAfterAt', label: 'Billing from' },
  { name: 'billingBeforeAt', label: 'Billing to' },
  { name: 'occursAfterAt', label: 'Occurs from' },
  { name: 'occursBeforeAt', label: 'Occurs to' }
];

const EXPORT_COLUMNS = [
  ['Id', 'Id'],
  ['Status', 'Status'],
  ['Type', 'Type'],
  ['InviteeName', 'Invitee'],
  ['InviterName', 'Inviter'],
  ['CreatedAtCet', 'Created (CET)'],
  ['OccursAtCet', 'Occurs (CET)'],
  ['PurchaseProductId', 'Purchase product id'],
  ['UserId', 'User id'],
  ['Reference', 'Reference'],
  ['MembershipSlots', 'Membership slots']
];

const FILTER_FIELDS = [
  ...TEXT_FILTERS,
  ...DATE_FILTERS,
  { name: 'facilityId', label: 'Facility' },
  { name: 'providerId', label: 'Provider' },
  { name: 'userId', label: 'User' },
  { name: 'status', label: 'Status' },
  { name: 'type', label: 'Type' }
];

const ParticipantsInternal = ({ api }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const query = useMemo(() => readQuery(location.search), [location.search]);

  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState(query);

  const page = parseInt(query.page, 10) || 1;
  // The same question the chips answer, so the empty row and the chips
  // can never disagree about whether anything is filtering the list.
  const orderBy = query.orderBy || SORTABLE.Created;
  const orderDirection = query.orderDirection === 'asc' ? 'asc' : 'desc';

  const filtered = FILTER_FIELDS.some(({ name: key }) => (
    key !== 'page' && key !== 'resultsPerPage' && query[key] !== undefined && query[key] !== ''
  ));

  const perPage = parseInt(query.resultsPerPage, 10) || 15;

  useEffect(() => {
    setDraft(query);
  }, [query]);

  const load = useCallback(async () => {
    setRows(null);
    setFailed(false);
    try {
      const data = await api.participants.searchParticipant({
        filters: query,
        skipPages: page,
        resultsCount: perPage
      });
      setRows(Array.isArray(data) ? data : (data?.Participants ?? []));
    } catch (error) {
      setFailed(true);
    }
  }, [api, query, page]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = (next, { resetPage = true } = {}) => {
    // The sort survives a search. It is not a filter — the filter row does not
    // hold it and `next` is built from that row — so applying one used to drop
    // the column somebody had just sorted by, silently, back to the default.
    const merged = {
      orderBy: query.orderBy,
      orderDirection: query.orderDirection,
      ...next
    };
    if (resetPage) delete merged.page;

    const search = Object.entries(merged)
      .filter(([, value]) => value !== '' && value != null)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    navigate(search ? `?${search}` : '', { replace: false });
  };

  const set = (name, value) => setDraft(previous => ({ ...previous, [name]: value }));

  // Clicking the sorted column flips the direction; a different column starts
  // newest-or-largest first.
  const sortBy = key => apply({
    ...query,
    orderBy: key,
    orderDirection: orderBy.toLowerCase() === key.toLowerCase() && orderDirection === 'desc' ? 'asc' : 'desc'
  });

  const sortMark = key =>
    (orderBy.toLowerCase() === key.toLowerCase() ? (orderDirection === 'desc' ? ' ↓' : ' ↑') : '');

  const header = (label, key, className) => (
    <th className={className}>
      {key ? (
        <button type="button" className="next-th-sort" onClick={() => sortBy(key)}>
          {label}{sortMark(key)}
        </button>
      ) : label}
    </th>
  );

  const textField = ({ name, label, placeholder }) => (
    <label className="next-filter" key={name}>
      <span className="next-filter__label">{label}</span>
      <input
        className="next-input"
        value={draft[name] ?? ''}
        placeholder={placeholder}
        onChange={event => set(name, event.target.value)}
      />
    </label>
  );

  const selectField = (name, label, options) => (
    <label className="next-filter" key={name}>
      <span className="next-filter__label">{label}</span>
      <select
        className="next-input"
        value={draft[name] ?? ''}
        onChange={event => set(name, event.target.value)}
      >
        <option value="">Any</option>
        {options.map(({ value, label: text }) => (
          <option key={value} value={value}>{text}</option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="next-page next-page--wide">
      <header className="next-page__header next-page__header--row">
        <div>
          <h1>Participants</h1>
          <p className="next-muted">
            {rows === null
              ? 'Loading…'
              : `Page ${page} · ${rows.length} row${rows.length === 1 ? '' : 's'} · newest first within the page`}
          </p>
        </div>
        <div className="next-page__actions">
          {/* The plus in the design, as a labelled button. */}
          <Link className="next-btn" to="/next/participant/new">Add participant</Link>
          <button type="button" className="next-btn" onClick={load}>Refresh</button>
        </div>
      </header>

      <section className="next-card">
        <form
          className="next-filters"
          onSubmit={event => { event.preventDefault(); apply(draft); }}
        >
          <div className="next-filters__row next-filters__row--grid">
            {TEXT_FILTERS.map(textField)}
            {selectField('type', 'Type', purchaseParticipantType)}
            {selectField('status', 'Status', purchaseParticipantStatus)}
          </div>

          <div className="next-filters__row next-filters__row--more">
            <LookupSelect
              label="Provider"
              value={draft.providerId}
              onChange={value => set('providerId', value)}
              loadOptions={key => lookupProvider(api, key)}
              placeholder="Name or id"
            />
            <LookupSelect
              label="Facility"
              value={draft.facilityId}
              onChange={value => set('facilityId', value)}
              loadOptions={key => lookupFacility(api, key)}
              placeholder="Name or id"
            />
            <LookupSelect
              label="User"
              value={draft.userId}
              onChange={value => set('userId', value)}
              loadOptions={key => lookupUser(api, key)}
              placeholder="Email or id"
            />
          </div>

          <div className="next-filters__row">
            {DATE_FILTERS.map(({ name, label }) => (
              <label className="next-filter" key={name}>
                <span className="next-filter__label">{label}</span>
                <input
                  className="next-input"
                  type="datetime-local"
                  value={draft[name] ?? ''}
                  onChange={event => set(name, event.target.value)}
                />
              </label>
            ))}

            <div className="next-filters__buttons">
              <button type="submit" className="next-btn next-btn--primary">Search</button>
              <button
                type="button"
                className="next-btn next-btn--quiet"
                onClick={() => { setDraft({}); apply({}); }}
              >
                Clear
              </button>
            </div>
          </div>
        </form>
      </section>

      <ActiveFilters
        fields={FILTER_FIELDS}
        query={query}
        onChange={next => { setDraft(next); apply(next); }}
      />

      <ListTools
        screenPath="/next/participants"
        search={location.search}
        columns={EXPORT_COLUMNS}
        rows={rows}
        exportName="participants"
        perPage={perPage}
        fetchPage={next => api.participants
          .searchParticipant({ filters: query, skipPages: next, resultsCount: perPage })
          .then(data => (Array.isArray(data) ? data : (data?.Participants ?? [])))}
      />

      {failed ? (
        <div className="next-note next-note--error">
          Could not load the participants.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      ) : (
        <section className="next-card">
          <div className="next-table-wrap">
            <table className="next-table next-table--wide next-table--stack">
              <thead>
                <tr>
                  <th>Id</th>
                  {header('Status', SORTABLE.Status)}
                  {header('Name', SORTABLE.Invitee)}
                  {header('Type', SORTABLE.Type)}
                  {header('Slots', SORTABLE.Slots, 'next-table__right')}
                  {/* The share is computed from a column the endpoint does not
                      sort on, so there is no arrow to give it. */}
                  <th className="next-table__right">Share</th>
                  {header('Created at (CET)', SORTABLE.Created)}
                  {header('Occurs at (CET)', SORTABLE.Occurs)}
                </tr>
              </thead>
              <tbody>
                {rows === null && (
                  <tr><td colSpan={8} className="next-muted next-table__note">Loading…</td></tr>
                )}
                {rows !== null && rows.length === 0 && (
                  <EmptyRow
                    columns={8}
                    noun="participant"
                    filtered={filtered}
                    onClear={() => { setDraft({}); apply({}); }}
                  />
                )}
                {(rows ?? []).map(row => (
                  <tr key={row.Id}>
                    <td data-label=""><Guid value={row.Id} to={`/next/participant/${row.Id}`} /></td>
                    <td data-label="Status">
                      {row.Status
                        ? <StatusPill status={row.Status}>{row.Status}</StatusPill>
                        : <span className="next-muted">—</span>}
                    </td>
                    <td className="next-stack__main" data-label="Name">{row.InviteeName || '—'}</td>
                    <td data-label="Type">
                      {row.Type
                        ? <span className="next-pill next-pill--neutral">{row.Type}</span>
                        : <span className="next-muted">—</span>}
                    </td>
                    <td className="next-table__right" data-label="Slots">
                      {row.MembershipSlots === null || row.MembershipSlots === undefined
                        ? <span className="next-muted">—</span>
                        : row.MembershipSlots}
                    </td>
                    <td className="next-table__right next-nowrap" data-label="Share">
                      {participantShare(row.ParticipantsIndex) ?? <span className="next-muted">—</span>}
                    </td>
                    <td className="next-nowrap next-muted" data-label="Created (CET)">{fmtDate(row.CreatedAtCet)}</td>
                    <td className="next-nowrap" data-label="Occurs (CET)">{fmtDate(row.OccursAtCet)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pager
            perPage={perPage}
            onPerPageChange={size => apply({ ...query, resultsPerPage: size })}
            page={page}
            hasNext={(rows ?? []).length >= perPage}
            onChange={next => apply({ ...query, page: next }, { resetPage: false })}
          />
        </section>
      )}
    </div>
  );
};

ParticipantsInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const Participants = withApi(ParticipantsInternal);

Participants.requiresLogin = true;

export default Participants;
